import { type TokenBalanceProps } from '~/components/component-library'
import { HIDDEN_CURRENCY_AMOUNT } from '~/store/privacyModeStore'

const formatterCache = new Map<string, Intl.NumberFormat>()

export const getFormatter = (currency: string) => {
  const cached = formatterCache.get(currency)
  if (cached) return cached
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  formatterCache.set(currency, formatter)
  return formatter
}

/** Masked text for a hidden balance. Keeps the sign so inflows still read as inflows. */
export const buildHiddenText = (
  direction: TokenBalanceProps['direction'],
  currency: string,
) => {
  const symbol =
    getFormatter(currency)
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? ''
  if (direction === 'inflow') return `+${symbol}${HIDDEN_CURRENCY_AMOUNT}`
  if (direction === 'outflow') return `-${symbol}${HIDDEN_CURRENCY_AMOUNT}`
  return `${symbol}${HIDDEN_CURRENCY_AMOUNT}`
}

export const buildValue = (value: number) => {
  const abs = Math.abs(value)
  if (abs === 0) return 0
  if (abs < 0.01) return 0.01
  return abs
}

const ROUNDING_THRESHOLD = 0.00995

export const buildPrefix = (
  direction: TokenBalanceProps['direction'],
  value: number,
) => {
  const abs = Math.abs(value)
  // Round up to $0.01 if >= $0.00995, otherwise show "<$0.01"
  const isLessThanOneCent = abs < ROUNDING_THRESHOLD && abs > 0

  if (direction === 'inflow') return `+${isLessThanOneCent ? '<' : ''}`
  if (direction === 'outflow') return `-${isLessThanOneCent ? '<' : ''}`
  return isLessThanOneCent ? '<' : ''
}
